import { useRef, useState } from 'react';
import { Bell, BellRing } from 'lucide-react';
import { Button, Description, Spinner } from '@heroui/react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
  getNotificationPreferences,
  notificationsKeys,
  setPushEnabledPreference,
} from '../../../api/notifications';
import { usePushNotifications } from '../../../hooks/usePushNotifications';
import { showError, showSuccess, showWarning } from '../../../lib/toast';
import { StatusCallout } from '../../../components/ui/status-callout';
import { getPushActivateErrorMessage } from '../utils/push-activate-error';
import { interpretPushActivation } from '../utils/push-activation';

interface PushPromptBannerProps {
  enabled: boolean;
}

export function PushPromptBanner({ enabled }: PushPromptBannerProps) {
  const queryClient = useQueryClient();
  const { isSupported, permission, status, register } = usePushNotifications();
  const [registering, setRegistering] = useState(false);
  const [localActivated, setLocalActivated] = useState(false);
  const registeringRef = useRef(false);

  const preferencesQuery = useQuery({
    queryKey: notificationsKeys.preferences(),
    queryFn: getNotificationPreferences,
    enabled,
    staleTime: 0,
  });

  if (!enabled || !isSupported) return null;

  const preferences = preferencesQuery.data;
  const showDenied = permission === 'denied';
  const showRegistered =
    localActivated ||
    status === 'registered' ||
    (permission === 'granted' && !!preferences?.hasPushToken);
  const showPrompt = !showDenied && !showRegistered;

  const handleActivate = async () => {
    if (registeringRef.current) return;
    registeringRef.current = true;
    setRegistering(true);
    try {
      // Um único gesto: register() pede permissão se faltar. requestPermission()
      // separado gasta o clique no iOS e o getToken falha em seguida.
      const outcome = interpretPushActivation(await register());
      if (outcome === 'denied') {
        showWarning(
          'Permita nas configurações do site ou do iPhone.',
          'Notificações bloqueadas',
        );
        return;
      }
      if (outcome === 'failed') {
        showError(getPushActivateErrorMessage(), 'Não foi possível ativar');
        return;
      }

      setLocalActivated(true);
      await setPushEnabledPreference(true).catch(() => undefined);
      await queryClient.invalidateQueries({ queryKey: notificationsKeys.preferences() });
      showSuccess('Você receberá avisos no dispositivo.', 'Notificações ativadas');
    } catch {
      const outcome = interpretPushActivation(false);
      if (outcome === 'denied') {
        showWarning(
          'Permita nas configurações do site ou do iPhone.',
          'Notificações bloqueadas',
        );
        return;
      }
      if (outcome === 'activated') {
        setLocalActivated(true);
        showSuccess('Você receberá avisos no dispositivo.', 'Notificações ativadas');
        return;
      }
      showError(getPushActivateErrorMessage(), 'Não foi possível ativar');
    } finally {
      registeringRef.current = false;
      setRegistering(false);
    }
  };

  if (preferencesQuery.isLoading) return null;

  if (showPrompt) {
    const needsReconnect = permission === 'granted' && !preferences?.hasPushToken;
    return (
      <div className="border-b border-separator px-4 py-3">
        <StatusCallout
          status="info"
          title={needsReconnect ? 'Reconectar avisos neste aparelho' : 'Receba avisos no dispositivo'}
          action={
            <Button
              size="sm"
              variant="primary"
              isDisabled={registering}
              onPress={() => void handleActivate()}
            >
              {registering ? <Spinner size="sm" /> : <Bell size={16} />}
              {needsReconnect ? 'Reconectar' : 'Ativar notificações'}
            </Button>
          }
        >
          {needsReconnect
            ? 'O iPhone já permite notificações, mas este aparelho ainda não está registrado no servidor. Toque em Reconectar.'
            : 'Ative as notificações do navegador para ser avisado mesmo com o app em segundo plano.'}
        </StatusCallout>
      </div>
    );
  }

  if (showDenied) {
    return (
      <div className="border-b border-separator px-4 py-3">
        <StatusCallout status="warning" title="Notificações bloqueadas">
          Use o ícone de cadeado na barra de endereço (ou Ajustes no iPhone) e permita notificações
          para este site.
        </StatusCallout>
      </div>
    );
  }

  if (showRegistered) {
    return (
      <div className="flex items-center gap-2 border-b border-separator px-4 py-2 text-sm text-accent">
        <BellRing size={16} className="shrink-0" />
        <Description className="text-accent">Notificações no navegador ativadas</Description>
      </div>
    );
  }

  return null;
}
